import { generateFileName } from "src/pages/Studies/transformer";
import { apiService } from "../../../services";
import { accessErrorTypes, fileTypes, IDownloadUrlData, IResponse, IStandardApiResponse } from "src/pages/Studies/models/interfaces";
import {
	ApiResponse,
	IAnalysisData,
	ICheckSegmentUpdatesPayload,
	ICheckSegmentUpdatesResponse,
	IMasterDropdownData,
	ITemplatePreviewResponse,
	ITemplateStatusResponse,
} from "../models/interfaces";
import { generateSplitByPayload, transformDataFilter } from "../transformer";
import { openForbiddenModal } from "src/pages/Studies/states/studiesSlice";
import { assertSafeHttpUrl } from "src/utils/security";
// import { mockMultiChartresp, sectionsResp } from "../components/QuantDashboard/Charts/chartResponse";

export const getDeepDiveLeftSectionData = async (study_id: string, insights_id: string) => {
	return await apiService.get("/insights/", { study_id, insights_id });
};

export const getDeepDiveRightSectionData = async (study_id: string, insights_id: string, ques_id: number) => {
	return await apiService.get("/insights/answers", {
		study_id,
		insights_id,
		ques_id,
	});
};

export const updateInsightsDetails = async (insightDetails) => {
	return await apiService.put(`/insights/update-insights`, insightDetails);
};

export const DeleteQuestion = async (insight_id: string, question_id: number | string, study_id) => {
	return await apiService.delete(`/insights/delete_question`, {
		study_id,
		insight_id,
		question_id,
	});
};

export const setLock = async (insight_id: string, question_id: number | string, study_id: string) => {
	return await apiService.post(`/insights/set-lock`, {
		study_id,
		insight_id,
		question_id,
	});
};

export const releaseLock = async (insight_id: string, question_id: number | string, study_id: string) => {
	return await apiService.post(`/insights/release-lock`, {
		study_id,
		insight_id,
		question_id,
	});
};

export const downloadFile = async (values, downloadInsightType, file_format, file_name, ques_id?: string | number) => {
	const fileName = generateFileName(file_format, file_name, ques_id);
	try {
		const response = await apiService.get<IStandardApiResponse<IDownloadUrlData>>(`/insights/export/${downloadInsightType}`, values);
		const downloadUrl = response.data?.url;
		if (!downloadUrl) {
			throw new Error("No download URL received");
		}
		const fileResponse = await fetch(assertSafeHttpUrl(downloadUrl));
		if (fileResponse.ok) {
			const blob = await fileResponse.blob(); // Convert the response to a blob
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = fileName; // Use the original file name for download
			document.body.appendChild(a); // Append the anchor to the body
			a.click(); // Trigger the download
			document.body.removeChild(a); // Remove the anchor from the body
			window.URL.revokeObjectURL(url); // Clean up the object URL
		} else {
			throw new Error("Failed to download file");
		}
	} catch (err) {
		console.error("Error downloading file: ", err);
		throw err;
	}
};

export const downloadStreamFile = async (data: any, file_name: string, url: string, type: string, dispatch: any, isChartPPTExport = false) => {
	try {
		const response: Blob = await apiService.post(url, isChartPPTExport ? data : { request: data }, { responseType: "blob" });

		const blob = new Blob([response], { type: type });
		const link = document.createElement("a");
		link.href = window.URL.createObjectURL(blob);
		link.download = file_name;
		document.body.appendChild(link);
		link.click();
		link.remove();
		window.URL.revokeObjectURL(link.href);

		return true;
	} catch (err: any) {
		console.error("downloadStreamFile: Error occurred", err);

		// Check if error response has blob data (403 errors with responseType: "blob")
		if (err?.response?.status === 403 && err?.response?.data instanceof Blob) {
			try {
				// Parse blob to get JSON error details
				const errorText = await err.response.data.text();
				const errorData = JSON.parse(errorText);
				const detail = errorData?.detail;

				const errorMessage = detail?.message || "Access forbidden";
				const errorType = detail?.error_type || accessErrorTypes.FEATURE_DISABLED;

				dispatch(openForbiddenModal({ message: errorMessage, type: errorType }));
				return false;
			} catch (parseError) {
				console.error("Error parsing blob error response:", parseError);
				dispatch(openForbiddenModal({ message: "Access forbidden", type: "FORBIDDEN" }));
				return false;
			}
		}

		// Handle other 403 errors (non-blob responses)
		if (err?.response?.status === 403) {
			const detail = err?.response?.data?.detail || err?.detail;
			const errorMessage = detail?.message || "Access forbidden";
			const errorType = detail?.error_type || accessErrorTypes.FEATURE_DISABLED;

			dispatch(openForbiddenModal({ message: errorMessage, type: errorType }));
			return false;
		}

		throw err;
	}
};

export const getSectionalSummaryData = async (study_id: String) => {
	return await apiService.post("quant/sectional/", {
		study_id: study_id,
	});
};

export const getQuantSections = async (study_id: String) => {
	return await apiService.get(`quant/sections?study_id=${study_id}`);
	//return sectionsResp;
};

export const getSectionalSummaryData2 = async (values: any) => {
	return await apiService.post("quant/sectional1/", { ...values });
};

export const getAnalysisData = async (
	searchtext: string,
	chartPanelDropdown: string,
	selectedSegments: any[] = [],
	filterArray: string[],
	pageNo: number,
	rowCount: number,
	masterDropDown: any,
	study_id: String,
): Promise<any> => {
	try {
		const response: IResponse = await apiService.post("quant/sectional/chart", {
			study_id: study_id,
			is_static: false,
			search_text: searchtext,
			chart_panel_dropdown: chartPanelDropdown,
			split_by: generateSplitByPayload(masterDropDown?.spilt_by, selectedSegments),
			filters: transformDataFilter(filterArray, masterDropDown?.add_filter),
			page_no: 1,
			row_count: rowCount,
			// search_text: Optional[str] = None
		});
		return {
			data: response.data,
			error: "",
			message: "Segment Data fetch successfully",
			is_error: false,
		};
	} catch (err) {
		console.error("Error analysis data: ", err);
	}
};

export const getAnalysisDataNew2 = async (values: any) => {
	return await apiService.post("quant/sectional/chart-new", { ...values });
};

export const getAnalysisDataNew = async (values: any, signal?: AbortSignal) => {
	const sanitizedValues = { ...values };
	if (!sanitizedValues?.slide_name?.trim()) {
		delete sanitizedValues.slide_name;
	}

	return await apiService.post("quant/sectional/chart1", sanitizedValues, { signal });
};

export const getSectionsSize = async (values: any) => {
	return await apiService.get("quant/study_size", { ...values });
};

export const getMasterDropdownSectionalSummary = async (study_id: String): Promise<any> => {
	return await apiService.get("quant/sectional/dropdown", { study_id });
};

export const getAllPPTTemplates = async (values: any) => {
	return await apiService.get("deck/templates", { ...values });
};

export const getPPTTemplateDetail = async (values: any) => {
	return await apiService.get("deck/templates", { ...values });
	// return {
	// 	message: "fetched successfully",
	// 	data: {
	// 		template_id: "1",
	// 		template_name: "Template 1",
	// 		template_description: "Description for Template 1",
	// 	},
	// 	error: null,
	// };
};

export const downloadPPTTemplate = async (values: any) => {
	const { file_name } = values;
	const fileName = generateFileName(fileTypes.ppt_template, file_name, "", "ppt");
	try {
		const response: any = await apiService.get("deck/templates", { ...values });
		const presignedUrl = response?.data?.presigned_url;
		if (!presignedUrl) throw new Error("Presigned URL not found in response");

		const fileResponse = await fetch(assertSafeHttpUrl(presignedUrl));
		if (fileResponse.ok) {
			const blob = await fileResponse.blob();
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = response?.data?.filename || fileName;
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			window.URL.revokeObjectURL(url);
		} else {
			throw new Error("Failed to download file");
		}
	} catch (err) {
		console.error("Error downloading file: ", err);
		throw err;
	}
};

export const getExecutiveSummaryData = async (study_id: string) => {
	return await apiService.post("quant/sectional1/", { study_id });
};

export const getCategoryData = async (study_id: string, insights_id: string) => {
	return await apiService.get(`quant/segment/category`, {
		study_id,
		insights_id,
	});
};

export const getListOfSegments = async (study_id: string) => {
	return await apiService.get(`/quant/segment/`, { study_id });
};

export const createSegment = async (segmentDetials) => {
	return await apiService.post(`/quant/segment/`, segmentDetials);
};

export const updateSegment = async (segmentDetials) => {
	return await apiService.put(`/quant/segment/`, segmentDetials);
};

export const getSegmentDetails = async (study_id: string, segment_id: string, reapply: boolean) => {
	return await apiService.get(`quant/segment/detail`, {
		study_id,
		segment_id,
		reapply: reapply,
	});
};

export const deleteSegment = async (study_id: string, segment_id: string) => {
	return await apiService.delete(`/quant/segment/`, { study_id, segment_id });
};

export const getNSizeOfSubSegment = async (segmentDetials) => {
	return await apiService.post(`quant/segment/n-size/`, segmentDetials);
};

export const getSectionalSearchSummaryData = async (study_id: string, search_text: string) => {
	return await apiService.get("quant/sectional/search", {
		study_id,
		search_text,
	});
};

export const reApplySegment = async (study_id: string, segment_id: string) => {
	return await apiService.post("quant/segment/reapply", {
		study_id,
		segment_id,
	});
};

export const getAnalysisAISummary = async (values: any) => {
	return await apiService.post("quant/chart/summary", { ...values });
};

export const getOpenEndedData = async (values: any) => {
	return await apiService.post("quant/sectional/open-end", { ...values });
};

export const generateSummary = async (values: any) => {
	return await apiService.post("/quant/generate-summary", { ...values });
};
export const generateSummaryStatus = async (values: any) => {
	return await apiService.post("/quant/summary-status", { ...values });
};

export const downloadCsvFile = async (
	searchtext: string,
	chartPanelDropdown: string,
	selectedSegments: any[] = [],
	filterArray: string[],
	pageNo: number,
	rowCount: number,
	masterDropDown: any,
	study_id: String,
) => {
	return await apiService.post("quant/export", {
		study_id: study_id,
		is_static: false,
		search_text: searchtext,
		chart_panel_dropdown: chartPanelDropdown,
		split_by: generateSplitByPayload(masterDropDown?.spilt_by, selectedSegments),
		filters: transformDataFilter(filterArray, masterDropDown?.add_filter),
		page_no: 1,
		row_count: rowCount,
		// search_text: Optional[str] = None
	});
};

export const getDashboardGlobalFilterCategories = async (study_id: string) => {
	return await apiService.get("quant/sectional/dropdown", { study_id });
};

export const getAnalysisSearchData = async (study_id: string, search_text: string) => {
	return await apiService.get(`quant/search?study_id=${study_id}&search_text=${search_text}`);
};

export const saveGlobalFilters = async (data: any) => {
	return await apiService.post("quant/globalFilters", { ...data });
};

export const storeUserPreferences = async (data: any) => {
	return await apiService.post("quant/filters", { ...data });
};

export const resetQuantDashboardAnalysis = async (data: any) => {
	return await apiService.post("quant/reset", { ...data });
};

export const quantDashboardExport = async (data: any) => {
	return await apiService.post(
		"quant/export",
		{ ...data },
		{
			responseType: "blob",
		},
	);
};

/**
 * Fetches the scaffold (layout metadata + chart stubs) for a multichart slide.
 * Returns only config data — no chart data is included.
 * Phase 2 calls getAnalysisDataNew() per stub for actual chart data.
 */
export const getSlideScaffold = async (
	payload: {
		study_id: string;
		slide_name: string;
		filters: any[];
		split_by: any[];
	},
	signal?: AbortSignal,
) => {
	return await apiService.post("/quant/sectional/slide/scaffold", payload, { signal });
};

/** Check if custom segments have been updated since last fetch */
export const checkSegmentUpdates = async (payload: ICheckSegmentUpdatesPayload, signal?: AbortSignal): Promise<ICheckSegmentUpdatesResponse> => {
	return await apiService.post("quant/segment/check-updates", payload, { signal });
};


/**
 * Polls the backend for current pipeline stage of the uploaded template.
 * GET /quant/templates/status?template_id=<ID>
 */
export const getTemplateProcessingStatus = async (templateId: string, signal?: AbortSignal): Promise<ITemplateStatusResponse> => {
	return await apiService.get("/quant/templates/status", { template_id: templateId }, { signal });
};

/**
 * Fetches the presigned download URL for the preview PPTX.
 * GET /quant/templates/preview?template_id=<ID>
 */
export const getTemplatePreviewUrl = async (templateId: string): Promise<ITemplatePreviewResponse> => {
	return await apiService.get("/quant/templates/preview", { template_id: templateId });
};

/**
 * Sets or unsets a template as the default for the profile.
 * POST /quant/templates/default — params in query, empty body.
 */
export const setTemplateAsDefault = async (templateId: string, isDefault: boolean): Promise<IResponse> => {
	return await apiService.post("/quant/templates/default", null, {
		params: { template_id: templateId, is_default: isDefault },
	});
};

/**
 * Initiates a retry upload for a rejected template.
 * POST /quant/templates/initiate?retry=true&template_id=<ID>
 * Reuses the same template_id; backend resets all pipeline state.
 */
export const initiateRetryTemplateUpload = async (
	templateId: string,
	fileDetails: { name: string; file_size: number; totalChunks: number; study_type_name: string }[],
	study_id: string,
): Promise<{ file_id: string; file_name: string; upload_id?: string; presigned_urls_info: Array<string | { url: string; part_number?: number }> }[]> => {
	return await apiService.post(
		"/quant/templates/initiate",
		{
			study_id,
			file_type: fileTypes.ppt_template,
			file_details: fileDetails,
		},
		{ params: { retry: true, template_id: templateId } },
	);
};

// Initiate V2 template upload — POST /quant/templates/initiate
// For new uploads: no query params. For retry: pass retry=true & template_id.
export const initiateTemplateUpload = async (
	fileDetails: { study_id: string; name: string; file_size: number; totalChunks: number }[],
	study_type_name: string,
): Promise<{ file_id: string; file_name: string; upload_id?: string; presigned_urls_info: Array<string | { url: string; part_number?: number }> }[]> => {
	return await apiService.post("/quant/templates/initiate", {
		study_id: fileDetails?.[0]?.study_id,
		file_type: fileTypes.ppt_template,
		file_details: fileDetails,
		study_type_name,
	});
};

// Complete V2 template upload — POST /quant/templates/complete
// Triggers the 4-stage AI pipeline. Poll /templates/status after this resolves.
export const completeTemplateUpload = async (payload: {
	study_id: string;
	file_id: string;
	file_name: string;
	total_chunks: number;
	is_error: boolean;
	upload_data: { upload_id: string; parts: { PartNumber: number; ETag: string }[] } | null;
}): Promise<IResponse> => {
	return await apiService.post("/quant/templates/complete", {
		file_type: fileTypes.ppt_template,
		...payload,
	});
};